// src/pages/AuthSso.tsx
//
// Platform (Mothership) SSO landing route. The platform launchpad
// ("Open As This School -> PIT") redirects the browser here as
//   https://pit.scr.vic.edu.au/auth/sso?code=<single-use-handoff-code>
//
// We hand the code to the `sso-exchange` edge function, which verifies it with
// the mothership and mints a Supabase session scoped to the LAUNCHED tenant,
// then we adopt that session so the app renders as that school (RLS-scoped).
// Direct Azure login is untouched — this is an additional entry path.
import { useEffect, useState } from 'react';
import { supabase } from '../supabase/config';

type Phase = 'working' | 'error';

export default function AuthSso() {
  const [phase, setPhase] = useState<Phase>('working');
  const [message, setMessage] = useState('Signing you in…');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const code = new URLSearchParams(window.location.search).get('code');
      if (!code) {
        // Nothing to exchange — send them to the normal login.
        window.location.replace('/login');
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('sso-exchange', {
          body: { code },
        });
        if (error) throw new Error(error.message || 'exchange_failed');
        const access_token = (data as any)?.access_token;
        const refresh_token = (data as any)?.refresh_token;
        if (!access_token || !refresh_token) {
          throw new Error((data as any)?.error || 'no_session_returned');
        }

        // Clear the cached PROFILE only — deliberately NOT the session.
        //
        // This used to call supabase.auth.signOut({ scope: 'local' }) first.
        // That is unsafe here for two reasons:
        //   1. This route renders INSIDE <AuthProvider> (App.tsx), so signOut
        //      fires a SIGNED_OUT event straight into the AuthContext that is
        //      about to hold the new session, and AuthContext treats any
        //      SIGNED_OUT as final (setUser(null)).
        //   2. supabase-js broadcasts auth events to every tab on this origin,
        //      so it also signed the user out of any other open PIT tab.
        // The net effect was a launch that appeared to succeed and then dropped
        // back to logged-out a moment later. setSession below replaces whatever
        // session was there, so the signOut was never needed.
        //
        // AuthContext caches the resolved user under 'pit_user_data'; clearing
        // it is what actually stops a prior school's profile lingering.
        try {
          Object.keys(localStorage)
            .filter((k) => k.startsWith('pit_'))
            .forEach((k) => localStorage.removeItem(k));
        } catch { /* ignore */ }

        // Adopt the tenant-scoped session (do this unconditionally — the session
        // switch is a global side effect that must complete even if this
        // component is unmounting).
        const { error: setErr } = await supabase.auth.setSession({
          access_token,
          refresh_token,
        });
        if (setErr) throw setErr;

        // Confirm the session is actually readable before navigating. setSession
        // persists asynchronously, and a hard redirect that wins that race
        // leaves the app booting with no session — which presents to the user
        // as "signed in, then signed out". Poll briefly rather than assume.
        let adopted = false;
        for (let attempt = 0; attempt < 10; attempt++) {
          const { data: check } = await supabase.auth.getSession();
          if (check?.session?.access_token) {
            adopted = true;
            break;
          }
          await new Promise((r) => setTimeout(r, 100));
        }
        if (!adopted) throw new Error('session_not_persisted');

        // Full reload so AuthContext re-initialises cleanly under the new session
        // with no stale cached user.
        window.location.replace('/dashboard');
      } catch (e) {
        if (cancelled) return;
        setPhase('error');
        setMessage(
          e instanceof Error && e.message
            ? `Could not complete the school launch (${e.message}).`
            : 'Could not complete the school launch.',
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-900 text-white p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="text-page-title font-serif">PIT</div>
        {phase === 'working' ? (
          <>
            <div className="animate-pulse text-lg">{message}</div>
            <div className="text-sm opacity-70">Connecting to the platform…</div>
          </>
        ) : (
          <>
            <div className="text-lg text-danger-fg">{message}</div>
            <a href="/login" className="inline-block underline text-sm opacity-90">
              Return to sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
}
